// Informe consolidado de un puesto (Anexo 1 GT 5, GT 6 y GT 7): las evaluaciones y mediciones que ya existen, los
// adjuntos (calibración, laboratorio y fotos), las medidas por jerarquía de controles (vinculadas a la Matriz de Riesgos
// o a Incidentes sin duplicar, o propias del puesto), conclusiones y recomendaciones, y las versiones aprobadas.
// Aprobar: firma electrónica simple del responsable de SSO (trazo, nombre y cédula; fecha y hora del servidor y SHA-256
// del contenido) o el formato impreso firmado a mano y su escaneo. Una versión aprobada no se edita: se aprueba otra.
// Atención prioritaria (GT 6): solo el número de trabajadores con condición considerada, nunca nombres ni tipo.

import React, { useMemo, useRef, useState } from 'react';
import SignaturePad, { type SignaturePadHandle } from '../permits/SignaturePad';
import {
  ACEPTA_ARCHIVO, actualizarMedida, aprobacionParaPdf, aprobarVersion, borrarAdjunto, borrarMedida, cerrarMedida, crearBorrador, crearMedidaPropia,
  evaluacionesDelInforme, guardarBorrador, inspeccionesFirmadas, medidasDelPuesto, medidasGT9, registrarVerificacion, subirAdjunto, urlArchivo, validarArchivo,
  vincularMedida, type DatosAdjunto, type DatosEmpresa, type InspeccionFirmada,
} from '../../lib/informes-puesto/ip-datos';
import { verificacionMedida } from '../../lib/informes-puesto/ip-checklist';
import {
  agruparPorJerarquia, candidatosMedidas, contenidoInforme, estadoInformePuesto, ETIQUETA_ESTADO_PUESTO, faltantesAprobacion, hashInforme, huellaEvaluaciones,
  isoDia, ultimaVersion,
} from '../../lib/informes-puesto/ip-logica';
import {
  CORTO_TIPO_INFORME, ETIQUETA_ADJUNTO, ETIQUETA_AGENTE, ETIQUETA_ESTADO_MEDIDA, ETIQUETA_MODULO, ETIQUETA_NIVEL, ETIQUETA_ORIGEN_MEDIDA,
  ETIQUETA_RESULTADO_VERIFICACION, ETIQUETA_TIPO_INFORME, NOTA_PLAZOS, NOTA_VERIFICACION, ORDEN_JERARQUIA, TEXTO_FIRMA_APROBACION, TIPOS_INFORME,
  type ContenidoInforme, type FilaMedida, type FilaVersion, type NivelControl, type ResultadoVerificacion, type TipoAdjunto, type TipoInforme,
} from '../../lib/informes-puesto/ip-tipos';
import { abrirConUrl, boton, chip, COLOR_ESTADO, fechaEc, msg, S } from './estilos';

// El PDF (jsPDF) se carga al usarlo.
const pdfs = () => import('../../lib/informes-puesto/ip-pdf');

export interface InformePuestoProps {
  companyId: string; empresa: string; datos: DatosEmpresa; puesto: { id: string; name: string }; tipoInicial: TipoInforme;
  /** Trabajadores del puesto con condición de atención prioritaria (null = no se puede leer con este rol). */
  prioritarios: number | null;
  puedeEditar: boolean; onCambio: () => Promise<void> | void; onCerrar: () => void;
}

const adjuntoVacio = (): DatosAdjunto & { archivo: File | null } => ({ kind: 'foto', title: '', archivo: null, agent_type: 'quimico', agent_name: '', result_value: null,
  limit_value: null, unit: '', limit_source: '', measured_at: '', provider: '', equipment: '', serial_number: '', valid_until: '' });

export default function InformePuestoModal(p: InformePuestoProps) {
  const [tipo, setTipo] = useState<TipoInforme>(p.tipoInicial);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const informe = p.datos.informes.find(i => i.job_position_id === p.puesto.id && i.kind === tipo) ?? null;
  const [conclusiones, setConclusiones] = useState(informe?.conclusions ?? '');
  const [recomendaciones, setRecomendaciones] = useState(informe?.recommendations ?? '');
  const [adj, setAdj] = useState(adjuntoVacio());
  const [nueva, setNueva] = useState<{ nivel: NivelControl; texto: string; prioritaria: boolean }>({ nivel: 'administrativo', texto: '', prioritaria: false });
  const [cierre, setCierre] = useState<{ id: string; fecha: string; resultado: string; archivo: File | null } | null>(null);
  const [verif, setVerif] = useState<{ id: string; fecha: string; resultado: ResultadoVerificacion; archivo: File | null; inspeccion: string; notas: string } | null>(null);
  const [inspecciones, setInspecciones] = useState<InspeccionFirmada[] | null>(null);
  const [metodo, setMetodo] = useState<'pantalla' | 'papel'>('pantalla');
  const [nombre, setNombre] = useState('');
  const [cedula, setCedula] = useState('');
  const [conTrazo, setConTrazo] = useState(false);
  const [escaneo, setEscaneo] = useState<File | null>(null);
  const pad = useRef<SignaturePadHandle>(null);

  const cambiarTipo = (t: TipoInforme) => {
    const inf = p.datos.informes.find(i => i.job_position_id === p.puesto.id && i.kind === t);
    setTipo(t); setConclusiones(inf?.conclusions ?? ''); setRecomendaciones(inf?.recommendations ?? ''); setError(''); setOk('');
  };
  const ejecutar = async (fn: () => Promise<void>, exito?: string) => {
    setOcupado(true); setError(''); setOk('');
    try { await fn(); await p.onCambio(); if (exito) setOk(exito); } catch (e) { setError(msg(e)); } finally { setOcupado(false); }
  };

  const evals = useMemo(() => evaluacionesDelInforme(p.datos, p.puesto.id, tipo), [p.datos, p.puesto.id, tipo]);
  const medidas = useMemo(() => medidasDelPuesto(p.datos, p.puesto.id), [p.datos, p.puesto.id]);
  const filasMedida = new Map(p.datos.medidas.map(m => [m.id, m]));
  // GT 9: verificación in situ de las medidas implementadas (plazos sugeridos por StorSafe, no normativos).
  const gt9 = useMemo(() => medidasGT9(p.datos), [p.datos]);
  const abrirVerificacion = (id: string) => {
    setVerif({ id, fecha: isoDia(new Date()), resultado: 'eficaz', archivo: null, inspeccion: '', notas: '' });
    if (!inspecciones) inspeccionesFirmadas(p.companyId).then(setInspecciones).catch(() => setInspecciones([]));
  };
  const bloqueVerificacion = (id: string) => {
    const mg = gt9.find(x => x.id === id);
    if (!mg) return null;
    const vm = verificacionMedida(mg, gt9, p.datos.verificaciones, new Date());
    const lista = p.datos.verificaciones.filter(v => v.measure_id === id).sort((a, b) => b.verified_on.localeCompare(a.verified_on) || b.created_at.localeCompare(a.created_at));
    const color = vm.item === 'cumple' ? '#2FBF71' : vm.item === 'no_cumple' ? '#f87171' : '#f59e0b';
    return (
      <div style={{ marginTop: 6, borderTop: '1px dashed #334155', paddingTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ ...S.nota, color }}><strong>Verificación in situ (GT 9):</strong> {vm.texto}</div>
        {lista.map(v => (
          <div key={v.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={S.nota}>{fechaEc(v.verified_on)} · {ETIQUETA_RESULTADO_VERIFICACION[v.result]}{v.inspection_id ? ' · con inspección firmada' : ''}{v.notes ? ` · ${v.notes}` : ''}</span>
            <button style={boton()} onClick={() => abrirConUrl(urlArchivo(v.evidence_path))}>Ver evidencia</button>
          </div>
        ))}
        {p.puedeEditar && verif?.id !== id && (
          <div style={S.botones}><button style={boton()} disabled={ocupado} onClick={() => abrirVerificacion(id)}>Registrar verificación in situ</button></div>
        )}
        {verif?.id === id && (
          <div style={S.rejilla}>
            <label style={S.etiqueta}>Fecha de la verificación<input type="date" style={S.input} max={isoDia(new Date())} value={verif.fecha} onChange={e => setVerif({ ...verif, fecha: e.target.value })} /></label>
            <label style={S.etiqueta}>Resultado
              <select style={S.input} value={verif.resultado} onChange={e => setVerif({ ...verif, resultado: e.target.value as ResultadoVerificacion })}>
                <option value="eficaz">Eficaz</option><option value="no_eficaz">No eficaz</option>
              </select>
            </label>
            <label style={{ ...S.etiqueta, flexBasis: '100%' }}>Foto o evidencia (obligatoria)
              <input type="file" accept={ACEPTA_ARCHIVO} onChange={e => setVerif({ ...verif, archivo: e.target.files?.[0] ?? null })} style={{ color: '#cbd5e1', maxWidth: '100%' }} />
            </label>
            <label style={{ ...S.etiqueta, flexBasis: '100%' }}>Inspección firmada (opcional)
              <select style={S.input} value={verif.inspeccion} onChange={e => setVerif({ ...verif, inspeccion: e.target.value })}>
                <option value="">Sin inspección vinculada</option>
                {(inspecciones ?? []).map(i => <option key={i.id} value={i.id}>{fechaEc(i.performed_date ?? i.signed_at)}{i.area ? ` · ${i.area}` : ''}</option>)}
              </select>
            </label>
            <label style={{ ...S.etiqueta, flexBasis: '100%' }}>Notas (opcional)<textarea style={{ ...S.input, minHeight: 60 }} maxLength={1000} value={verif.notas} onChange={e => setVerif({ ...verif, notas: e.target.value })} /></label>
            <p style={{ ...S.nota, flexBasis: '100%' }}>{NOTA_VERIFICACION} Una verificación registrada no se edita ni se borra.</p>
            <div style={S.botones}>
              <button style={boton('primario', ocupado)} disabled={ocupado} onClick={() => ejecutar(async () => {
                if (!verif.archivo) throw new Error('Sube la foto o evidencia de la verificación.');
                if (!verif.fecha || verif.fecha > isoDia(new Date())) throw new Error('La fecha de la verificación no puede ser futura.');
                await registrarVerificacion(p.companyId, id, { fecha: verif.fecha, resultado: verif.resultado, evidencia: verif.archivo, inspeccionId: verif.inspeccion || null, notas: verif.notas });
                setVerif(null);
              }, 'Verificación in situ registrada.')}>Guardar verificación</button>
              <button style={boton()} onClick={() => setVerif(null)}>Cancelar</button>
            </div>
          </div>
        )}
      </div>
    );
  };
  const adjuntos = informe ? p.datos.adjuntos.filter(a => a.report_id === informe.id) : [];
  const versiones = informe ? p.datos.versiones.filter(v => v.report_id === informe.id).sort((a, b) => b.version - a.version) : [];
  const ultima = ultimaVersion(versiones);
  const candidatos = candidatosMedidas(p.puesto.id, p.datos.iper, p.datos.acciones, p.datos.medidas.filter(m => m.job_position_id === p.puesto.id));
  const expuestos = p.datos.activosPorPuesto[p.puesto.id] ?? 0;

  const armar = (): ContenidoInforme | null => informe ? contenidoInforme({ informe: { ...informe, conclusions: conclusiones, recommendations: recomendaciones },
    puestoId: p.puesto.id, empresa: p.empresa, hoy: new Date(), expuestos, evaluaciones: evals, adjuntos, medidas,
    prioritarios: tipo === 'riesgos' ? p.prioritarios : null }) : null;
  const contenido = armar();
  const faltan = contenido ? faltantesAprobacion(contenido) : [];
  const [estadoVista, setEstadoVista] = useState<string>('');
  React.useEffect(() => {
    let vivo = true;
    huellaEvaluaciones(evals).then(h => {
      if (!vivo) return;
      const r = estadoInformePuesto(!!informe, ultima, h, evals, new Date());
      setEstadoVista(r.estado + (r.vencidoDesde ? `|${r.vencidoDesde}|${r.motivo}` : ''));
    });
    return () => { vivo = false; };
  }, [evals, informe, ultima]);
  const [est, vencidoDesde, motivo] = estadoVista.split('|');

  // ── Acciones ──
  const crear = () => ejecutar(async () => { await crearBorrador(p.companyId, p.puesto.id, tipo, p.puesto.name); }, 'Informe creado como borrador.');
  const guardar = () => ejecutar(async () => { await guardarBorrador(informe!.id, { conclusions: conclusiones, recommendations: recomendaciones }); }, 'Borrador guardado.');
  const subir = () => ejecutar(async () => {
    if (!adj.archivo) throw new Error('Elige el archivo.');
    if (!adj.title.trim()) throw new Error('Escribe un título para el adjunto.');
    const e = validarArchivo(adj.archivo); if (e) throw new Error(e);
    const { archivo, ...d } = adj;
    const limpio: DatosAdjunto = { kind: d.kind, title: d.title.trim() };
    if (d.kind === 'laboratorio') {
      if (!d.agent_name?.trim() || d.result_value === null || d.limit_value === null || !d.limit_source?.trim() || !d.measured_at)
        throw new Error('Para un informe de laboratorio indica el agente, el resultado, el límite, su fuente y la fecha.');
      Object.assign(limpio, { agent_type: d.agent_type, agent_name: d.agent_name.trim(), result_value: d.result_value, limit_value: d.limit_value,
        unit: d.unit?.trim() || null, limit_source: d.limit_source.trim(), measured_at: d.measured_at, provider: d.provider?.trim() || null });
    } else if (d.kind === 'calibracion') {
      Object.assign(limpio, { equipment: d.equipment?.trim() || null, serial_number: d.serial_number?.trim() || null, provider: d.provider?.trim() || null,
        measured_at: d.measured_at || null, valid_until: d.valid_until || null });
    }
    await subirAdjunto(p.companyId, informe!.id, archivo, limpio);
    setAdj(adjuntoVacio());
  }, 'Adjunto subido.');
  const aprobar = () => ejecutar(async () => {
    if (!informe) return;
    if (conclusiones !== (informe.conclusions ?? '') || recomendaciones !== (informe.recommendations ?? ''))
      await guardarBorrador(informe.id, { conclusions: conclusiones, recommendations: recomendaciones });
    const c = armar()!;
    const f = faltantesAprobacion(c); if (f.length) throw new Error(f.join(' '));
    const hash = await hashInforme(c);
    const huella = await huellaEvaluaciones(evals);
    if (metodo === 'pantalla') {
      if (!conTrazo || pad.current?.isEmpty()) throw new Error('Firma en el recuadro.');
      const trazo = await pad.current!.toBlob(); if (!trazo) throw new Error('No se pudo leer la firma.');
      await aprobarVersion(p.companyId, { tipo: 'informe_puesto', informeId: informe.id, kind: tipo, huella }, c, hash, { metodo: 'pantalla', nombre, cedula, trazo });
    } else {
      if (!escaneo) throw new Error('Sube el escaneo del formato firmado.');
      await aprobarVersion(p.companyId, { tipo: 'informe_puesto', informeId: informe.id, kind: tipo, huella }, c, hash, { metodo: 'papel', escaneo });
    }
    pad.current?.clear(); setConTrazo(false); setEscaneo(null);
  }, 'Versión aprobada. Ya no se modifica: para cambiar algo, aprueba una versión nueva.');

  const pdfVersion = async (v: FilaVersion) => {
    try {
      const { generarInformePuestoPDF, nombreArchivo } = await pdfs();
      const doc = generarInformePuestoPDF({ contenido: v.snapshot as unknown as ContenidoInforme, hash: v.content_hash, aprobacion: await aprobacionParaPdf(v), blanco: false, fecha: new Date() });
      doc.save(nombreArchivo(`${CORTO_TIPO_INFORME[tipo]}_${p.puesto.name}`, `_v${v.version}`));
    } catch (e) { setError(msg(e)); }
  };
  const pdfBorrador = async (blanco: boolean) => {
    const c = armar(); if (!c) return;
    const { generarInformePuestoPDF, nombreArchivo } = await pdfs();
    const doc = generarInformePuestoPDF({ contenido: c, hash: blanco ? await hashInforme(c) : null, aprobacion: null, blanco, fecha: new Date() });
    doc.save(nombreArchivo(`${CORTO_TIPO_INFORME[tipo]}_${p.puesto.name}`, blanco ? '_para_firmar' : '_borrador'));
  };

  const editable = p.puedeEditar && !ocupado;
  const filaSeguimiento = (fm: FilaMedida) => (
    <div style={{ ...S.rejilla, marginTop: 6 }}>
      <label style={S.etiqueta}>Responsable
        <input style={S.input} defaultValue={fm.responsible_name ?? ''} disabled={!editable || fm.status === 'cerrada'}
          onBlur={e => e.target.value !== (fm.responsible_name ?? '') && ejecutar(() => actualizarMedida(fm, { responsible_name: e.target.value.trim() || null }))} />
      </label>
      <label style={S.etiqueta}>Fecha compromiso
        <input type="date" style={S.input} defaultValue={fm.due_date ?? ''} disabled={!editable || fm.status === 'cerrada'}
          onBlur={e => e.target.value !== (fm.due_date ?? '') && ejecutar(() => actualizarMedida(fm, { due_date: e.target.value || null }))} />
      </label>
      <label style={S.etiqueta}>Estado
        <select style={S.input} value={fm.status === 'cerrada' ? 'cerrada' : fm.status} disabled={!editable || fm.status === 'cerrada'}
          onChange={e => ejecutar(() => actualizarMedida(fm, { status: e.target.value as FilaMedida['status'] }))}>
          <option value="pendiente">Pendiente</option><option value="en_curso">En curso</option>
          {fm.status === 'cerrada' && <option value="cerrada">Cerrada</option>}
        </select>
      </label>
      <label style={{ ...S.etiqueta, flexBasis: '100%' }}>Seguimiento
        <textarea style={{ ...S.input, minHeight: 50 }} defaultValue={fm.follow_up ?? ''} disabled={!editable || fm.status === 'cerrada'}
          onBlur={e => e.target.value !== (fm.follow_up ?? '') && ejecutar(() => actualizarMedida(fm, { follow_up: e.target.value.trim() || null }))} />
      </label>
    </div>
  );

  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && !ocupado && p.onCerrar()} role="dialog" aria-modal="true" aria-label={`Informes del puesto ${p.puesto.name}`}>
      <div style={S.caja}>
        <div style={S.cabecera}>
          <div style={{ minWidth: 0 }}>
            <h2 style={S.h2}>{p.puesto.name}</h2>
            <p style={S.nota}>{expuestos === 1 ? '1 trabajador activo' : `${expuestos} trabajadores activos`} · {ETIQUETA_TIPO_INFORME[tipo]}</p>
          </div>
          <button style={boton()} onClick={p.onCerrar} disabled={ocupado} aria-label="Cerrar">✕</button>
        </div>
        <div style={S.cuerpo}>
          <div style={S.pestanas} role="tablist">
            {TIPOS_INFORME.map(t => (
              <button key={t} role="tab" aria-selected={t === tipo} style={{ ...boton(t === tipo ? 'primario' : 'secundario'), flex: '1 1 100px' }} onClick={() => cambiarTipo(t)}>
                {CORTO_TIPO_INFORME[t]}
              </button>
            ))}
          </div>
          {error && <p style={S.error} role="alert">{error}</p>}
          {ok && <p style={S.ok} role="status">{ok}</p>}

          {!informe ? (
            <div style={S.bloque}>
              <p style={S.texto}>Este puesto todavía no tiene el {ETIQUETA_TIPO_INFORME[tipo].toLowerCase()}.</p>
              {p.puedeEditar ? <button style={boton('primario', ocupado)} disabled={ocupado} onClick={crear}>Crear el informe</button>
                : <p style={S.nota}>Solo el administrador o el responsable pueden crearlo.</p>}
            </div>
          ) : (
            <>
              <div style={S.bloque}>
                <div style={S.fila}>
                  <span style={S.h3}>Estado</span>
                  {est && <span style={chip(COLOR_ESTADO[est] ?? '#94a3b8')}>{ETIQUETA_ESTADO_PUESTO[est as keyof typeof ETIQUETA_ESTADO_PUESTO]}</span>}
                </div>
                {vencidoDesde && <p style={S.nota}>{motivo === 'cambio' ? `Una evaluación del puesto cambió después de la aprobación (desde el ${fechaEc(vencidoDesde)}): aprueba una versión nueva.`
                  : `La vigencia terminó el ${fechaEc(vencidoDesde)}: aprueba una versión nueva.`}</p>}
                <p style={S.nota}>{NOTA_PLAZOS}</p>
              </div>

              {tipo !== 'medidas' && (
                <div style={S.bloque}>
                  <h3 style={S.h3}>{tipo === 'agentes' ? 'Mediciones y evaluaciones de agentes' : 'Evaluaciones de riesgo'} ({evals.length})</h3>
                  {!evals.length && <p style={S.nota}>El puesto no tiene evaluaciones para este informe en los módulos de StorSafe.</p>}
                  {evals.map(e => (
                    <div key={`${e.modulo}-${e.id}`} style={S.fila}>
                      <div style={{ minWidth: 0, flex: '1 1 220px' }}>
                        <div style={{ ...S.texto, fontWeight: 600 }}>{ETIQUETA_MODULO[e.modulo]} · {e.titulo}</div>
                        <div style={S.nota}>{ETIQUETA_AGENTE[e.agente]} · {fechaEc(e.fecha)} · {e.metodologia}</div>
                        <div style={S.nota}>{e.resultado} · Referencia: {e.referencia}</div>
                      </div>
                      <span style={chip(e.cumple === null ? '#94a3b8' : e.cumple ? '#2FBF71' : '#f87171')}>{e.cumple === null ? 'Sin criterio' : e.cumple ? 'Cumple' : 'No cumple'}</span>
                    </div>
                  ))}
                </div>
              )}

              {tipo === 'riesgos' && (
                <div style={S.bloque}>
                  <h3 style={S.h3}>Grupos de atención prioritaria</h3>
                  <p style={S.texto}>{p.prioritarios === null ? 'El número de trabajadores con condición de atención prioritaria solo lo ven el administrador y el responsable.'
                    : `${p.prioritarios === 1 ? '1 trabajador' : `${p.prioritarios} trabajadores`} con condición de atención prioritaria considerada.`}</p>
                  <p style={S.nota}>El informe nunca muestra nombres ni tipo de condición. Las medidas para este grupo se agregan abajo marcando "Para el grupo de atención prioritaria".</p>
                  {medidas.filter(m => m.prioritaria).map(m => <p key={m.id} style={S.texto}>• {m.nivel ? `${ETIQUETA_NIVEL[m.nivel]}: ` : ''}{m.descripcion}</p>)}
                </div>
              )}

              {tipo !== 'medidas' && (
                <div style={S.bloque}>
                  <h3 style={S.h3}>Adjuntos ({adjuntos.length})</h3>
                  <p style={S.nota}>Certificados de calibración de los equipos, informes externos de laboratorio (agentes químicos y biológicos) y fotos. PDF, PNG o JPEG hasta 10 MB.</p>
                  {adjuntos.map(a => (
                    <div key={a.id} style={S.fila}>
                      <div style={{ minWidth: 0, flex: '1 1 220px' }}>
                        <div style={{ ...S.texto, fontWeight: 600 }}>{ETIQUETA_ADJUNTO[a.kind]}: {a.title}</div>
                        {a.kind === 'laboratorio' && <div style={S.nota}>{a.agent_name}: {String(a.result_value).replace('.', ',')} {a.unit ?? ''} frente a {String(a.limit_value).replace('.', ',')} {a.unit ?? ''} ({a.limit_source}) · {Number(a.result_value) <= Number(a.limit_value) ? 'Cumple' : 'No cumple'}</div>}
                        {a.kind === 'calibracion' && <div style={S.nota}>{[a.equipment, a.serial_number && `serie ${a.serial_number}`, a.valid_until && `vigente hasta ${fechaEc(a.valid_until)}`].filter(Boolean).join(' · ')}</div>}
                      </div>
                      <div style={S.botones}>
                        <button style={boton()} onClick={() => abrirConUrl(urlArchivo(a.file_path))}>Ver</button>
                        {p.puedeEditar && <button style={boton('peligro', ocupado)} disabled={ocupado} onClick={() => window.confirm('¿Borrar este adjunto?') && ejecutar(() => borrarAdjunto(a.id), 'Adjunto borrado.')}>Borrar</button>}
                      </div>
                    </div>
                  ))}
                  {p.puedeEditar && (
                    <div style={{ ...S.rejilla, borderTop: '1px solid #334155', paddingTop: 10 }}>
                      <label style={S.etiqueta}>Tipo
                        <select style={S.input} value={adj.kind} onChange={e => setAdj({ ...adj, kind: e.target.value as TipoAdjunto })}>
                          {(['calibracion', 'laboratorio', 'foto'] as TipoAdjunto[]).map(k => <option key={k} value={k}>{ETIQUETA_ADJUNTO[k]}</option>)}
                        </select>
                      </label>
                      <label style={S.etiqueta}>Título<input style={S.input} value={adj.title} onChange={e => setAdj({ ...adj, title: e.target.value })} /></label>
                      {adj.kind === 'laboratorio' && (<>
                        <label style={S.etiqueta}>Agente
                          <select style={S.input} value={adj.agent_type ?? 'quimico'} onChange={e => setAdj({ ...adj, agent_type: e.target.value as 'quimico' | 'biologico' })}>
                            <option value="quimico">Químico</option><option value="biologico">Biológico</option>
                          </select>
                        </label>
                        <label style={S.etiqueta}>Nombre del agente<input style={S.input} value={adj.agent_name ?? ''} onChange={e => setAdj({ ...adj, agent_name: e.target.value })} /></label>
                        <label style={S.etiqueta}>Resultado<input inputMode="decimal" style={S.input} value={adj.result_value ?? ''} onChange={e => setAdj({ ...adj, result_value: e.target.value === '' ? null : Number(e.target.value.replace(',', '.')) })} /></label>
                        <label style={S.etiqueta}>Límite<input inputMode="decimal" style={S.input} value={adj.limit_value ?? ''} onChange={e => setAdj({ ...adj, limit_value: e.target.value === '' ? null : Number(e.target.value.replace(',', '.')) })} /></label>
                        <label style={S.etiqueta}>Unidad<input style={S.input} value={adj.unit ?? ''} onChange={e => setAdj({ ...adj, unit: e.target.value })} /></label>
                        <label style={S.etiqueta}>Fuente del límite<input style={S.input} value={adj.limit_source ?? ''} onChange={e => setAdj({ ...adj, limit_source: e.target.value })} placeholder="Norma o documento de donde sale" /></label>
                        <label style={S.etiqueta}>Fecha de la medición<input type="date" style={S.input} value={adj.measured_at ?? ''} onChange={e => setAdj({ ...adj, measured_at: e.target.value })} /></label>
                        <label style={S.etiqueta}>Laboratorio<input style={S.input} value={adj.provider ?? ''} onChange={e => setAdj({ ...adj, provider: e.target.value })} /></label>
                      </>)}
                      {adj.kind === 'calibracion' && (<>
                        <label style={S.etiqueta}>Equipo<input style={S.input} value={adj.equipment ?? ''} onChange={e => setAdj({ ...adj, equipment: e.target.value })} /></label>
                        <label style={S.etiqueta}>Serie<input style={S.input} value={adj.serial_number ?? ''} onChange={e => setAdj({ ...adj, serial_number: e.target.value })} /></label>
                        <label style={S.etiqueta}>Vigente hasta<input type="date" style={S.input} value={adj.valid_until ?? ''} onChange={e => setAdj({ ...adj, valid_until: e.target.value })} /></label>
                      </>)}
                      <label style={{ ...S.etiqueta, flexBasis: '100%' }}>Archivo
                        <input type="file" accept={ACEPTA_ARCHIVO} onChange={e => setAdj({ ...adj, archivo: e.target.files?.[0] ?? null })} style={{ color: '#cbd5e1', maxWidth: '100%' }} />
                      </label>
                      <button style={boton('primario', ocupado)} disabled={ocupado} onClick={subir}>Subir adjunto</button>
                    </div>
                  )}
                </div>
              )}

              {(tipo === 'medidas' || tipo === 'riesgos') && (
                <div style={S.bloque}>
                  <h3 style={S.h3}>{tipo === 'medidas' ? `Medidas por jerarquía de controles (${medidas.length})` : `Medidas para el grupo de atención prioritaria (${medidas.filter(m => m.prioritaria).length})`}</h3>
                  {tipo === 'medidas' && <p style={S.nota}>Las medidas de la Matriz de Riesgos y de Incidentes se vinculan sin copiarlas: el texto sale de su origen y las de Incidentes llevan su seguimiento y cierre en Incidentes.</p>}
                  {agruparPorJerarquia(tipo === 'medidas' ? medidas : medidas.filter(m => m.prioritaria)).map(g => (
                    <div key={g.nivel ?? 'sin'}>
                      <div style={{ ...S.texto, fontWeight: 700, color: '#93c5fd', marginTop: 6 }}>{g.nivel ? ETIQUETA_NIVEL[g.nivel] : 'Sin nivel de control'}</div>
                      {g.medidas.map(m => {
                        const fm = filasMedida.get(m.id)!;
                        return (
                          <div key={m.id} style={{ ...S.fila, flexDirection: 'column', alignItems: 'stretch' }}>
                            {/* alignItems: sin él la etiqueta de estado se estira a la altura del texto (con 2 o 3 líneas, un óvalo grande). */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                              <div style={{ minWidth: 0, flex: '1 1 220px' }}>
                                <div style={{ ...S.texto, fontWeight: 600 }}>{m.descripcion}</div>
                                <div style={S.nota}>{ETIQUETA_ORIGEN_MEDIDA[m.origen]}{m.prioritaria ? ' · para el grupo de atención prioritaria' : ''}{m.origen === 'incidente' ? ` · ${m.responsable || 'sin responsable'} · ${fechaEc(m.compromiso)}` : ''}</div>
                                {m.estado === 'cerrada' && <div style={S.nota}>Cerrada el {fechaEc(m.cerrada_el)}{m.evidencia ? ` · evidencia: ${m.evidencia}` : ''}{m.resultado ? ` · ${m.resultado}` : ''}</div>}
                              </div>
                              <span style={chip(COLOR_ESTADO[m.estado])}>{ETIQUETA_ESTADO_MEDIDA[m.estado]}</span>
                            </div>
                            {tipo === 'medidas' && fm.source !== 'incidente' && filaSeguimiento(fm)}
                            {tipo === 'medidas' && p.puedeEditar && (
                              <div style={{ ...S.botones, marginTop: 6 }}>
                                <label style={{ ...S.nota, display: 'flex', gap: 6, alignItems: 'center' }}>
                                  <input type="checkbox" checked={fm.for_priority_group} disabled={!editable} onChange={e => ejecutar(() => actualizarMedida(fm, { for_priority_group: e.target.checked }))} />
                                  Para el grupo de atención prioritaria
                                </label>
                                {fm.source !== 'incidente' && fm.status !== 'cerrada' && <button style={boton()} disabled={!editable} onClick={() => setCierre({ id: fm.id, fecha: isoDia(new Date()), resultado: '', archivo: null })}>Cerrar con evidencia</button>}
                                {fm.evidence_path && <button style={boton()} onClick={() => abrirConUrl(urlArchivo(fm.evidence_path!))}>Ver evidencia</button>}
                                {fm.status !== 'cerrada' && <button style={boton('peligro', !editable)} disabled={!editable} onClick={() => window.confirm('¿Quitar esta medida del puesto?') && ejecutar(() => borrarMedida(fm.id), 'Medida quitada.')}>Quitar</button>}
                              </div>
                            )}
                            {cierre?.id === fm.id && (
                              <div style={{ ...S.rejilla, marginTop: 6 }}>
                                <label style={S.etiqueta}>Fecha de cierre<input type="date" style={S.input} value={cierre.fecha} onChange={e => setCierre({ ...cierre, fecha: e.target.value })} /></label>
                                <label style={S.etiqueta}>Resultado<input style={S.input} value={cierre.resultado} onChange={e => setCierre({ ...cierre, resultado: e.target.value })} /></label>
                                <label style={{ ...S.etiqueta, flexBasis: '100%' }}>Evidencia (foto o PDF)
                                  <input type="file" accept={ACEPTA_ARCHIVO} onChange={e => setCierre({ ...cierre, archivo: e.target.files?.[0] ?? null })} style={{ color: '#cbd5e1', maxWidth: '100%' }} />
                                </label>
                                <div style={S.botones}>
                                  <button style={boton('primario', ocupado)} disabled={ocupado} onClick={() => ejecutar(async () => {
                                    if (!cierre.archivo) throw new Error('Sube la evidencia del cierre.');
                                    await cerrarMedida(p.companyId, fm, cierre.archivo, cierre.fecha, cierre.resultado); setCierre(null);
                                  }, 'Medida cerrada con evidencia.')}>Guardar cierre</button>
                                  <button style={boton()} onClick={() => setCierre(null)}>Cancelar</button>
                                </div>
                              </div>
                            )}
                            {tipo === 'medidas' && m.estado === 'cerrada' && bloqueVerificacion(m.id)}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  {tipo === 'medidas' && p.puedeEditar && (candidatos.deIper.length + candidatos.deIncidentes.length > 0) && (
                    <div style={{ borderTop: '1px solid #334155', paddingTop: 8 }}>
                      <div style={S.h3}>Para vincular</div>
                      {candidatos.deIper.map(c => (
                        <div key={`${c.iper_id}-${c.nivel}`} style={S.fila}>
                          <div style={{ minWidth: 0, flex: '1 1 200px' }}><div style={S.texto}>{c.texto}</div><div style={S.nota}>Matriz de Riesgos · {ETIQUETA_NIVEL[c.nivel]}{c.de ? ` · ${c.de}` : ''}</div></div>
                          <button style={boton('secundario', ocupado)} disabled={ocupado} onClick={() => ejecutar(() => vincularMedida(p.companyId, p.puesto.id, { origen: 'iper', iper_id: c.iper_id, nivel: c.nivel }), 'Medida vinculada.')}>Vincular</button>
                        </div>
                      ))}
                      {candidatos.deIncidentes.map(c => (
                        <div key={c.accion_id} style={S.fila}>
                          <div style={{ minWidth: 0, flex: '1 1 200px' }}><div style={S.texto}>{c.texto}</div><div style={S.nota}>Incidentes · {c.nivel ? ETIQUETA_NIVEL[c.nivel] : 'sin nivel'}</div></div>
                          <button style={boton('secundario', ocupado)} disabled={ocupado} onClick={() => ejecutar(() => vincularMedida(p.companyId, p.puesto.id, { origen: 'incidente', accion_id: c.accion_id }), 'Medida vinculada.')}>Vincular</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {p.puedeEditar && (
                    <div style={{ ...S.rejilla, borderTop: '1px solid #334155', paddingTop: 10 }}>
                      <label style={S.etiqueta}>Nivel
                        <select style={S.input} value={nueva.nivel} onChange={e => setNueva({ ...nueva, nivel: e.target.value as NivelControl })}>
                          {ORDEN_JERARQUIA.map(n => <option key={n} value={n}>{ETIQUETA_NIVEL[n]}</option>)}
                        </select>
                      </label>
                      <label style={{ ...S.etiqueta, flex: '2 1 220px' }}>Medida propia del puesto<input style={S.input} value={nueva.texto} onChange={e => setNueva({ ...nueva, texto: e.target.value })} /></label>
                      <label style={{ ...S.nota, display: 'flex', gap: 6, alignItems: 'center', flexBasis: '100%' }}>
                        <input type="checkbox" checked={nueva.prioritaria || tipo === 'riesgos'} disabled={tipo === 'riesgos'} onChange={e => setNueva({ ...nueva, prioritaria: e.target.checked })} />
                        Para el grupo de atención prioritaria
                      </label>
                      <button style={boton('primario', ocupado)} disabled={ocupado} onClick={() => ejecutar(async () => {
                        if (nueva.texto.trim().length < 3) throw new Error('Escribe la medida.');
                        await crearMedidaPropia(p.companyId, p.puesto.id, nueva.nivel, nueva.texto, nueva.prioritaria || tipo === 'riesgos');
                        setNueva({ ...nueva, texto: '' });
                      }, 'Medida agregada.')}>Agregar medida</button>
                    </div>
                  )}
                </div>
              )}

              <div style={S.bloque}>
                <h3 style={S.h3}>Conclusiones y recomendaciones</h3>
                <label style={S.etiqueta}>Conclusiones<textarea style={{ ...S.input, minHeight: 80 }} value={conclusiones} disabled={!p.puedeEditar} onChange={e => setConclusiones(e.target.value)} /></label>
                <label style={S.etiqueta}>Recomendaciones<textarea style={{ ...S.input, minHeight: 80 }} value={recomendaciones} disabled={!p.puedeEditar} onChange={e => setRecomendaciones(e.target.value)} /></label>
                <div style={S.botones}>
                  {p.puedeEditar && <button style={boton('secundario', ocupado)} disabled={ocupado} onClick={guardar}>Guardar borrador</button>}
                  <button style={boton()} onClick={() => pdfBorrador(false)}>PDF del borrador</button>
                </div>
              </div>

              {p.puedeEditar && (
                <div style={S.bloque}>
                  <h3 style={S.h3}>Aprobar la versión {(ultima?.version ?? 0) + 1}</h3>
                  {faltan.length > 0 && <ul style={{ margin: 0, paddingLeft: 18 }}>{faltan.map(f => <li key={f} style={{ ...S.nota, color: '#fbbf24' }}>{f}</li>)}</ul>}
                  <div style={S.pestanas}>
                    <button style={boton(metodo === 'pantalla' ? 'primario' : 'secundario')} onClick={() => setMetodo('pantalla')}>Firmar en pantalla</button>
                    <button style={boton(metodo === 'papel' ? 'primario' : 'secundario')} onClick={() => setMetodo('papel')}>Firma manual</button>
                  </div>
                  {metodo === 'pantalla' ? (<>
                    <div style={S.rejilla}>
                      <label style={S.etiqueta}>Nombre del responsable de SSO<input style={S.input} value={nombre} onChange={e => setNombre(e.target.value)} /></label>
                      <label style={S.etiqueta}>Cédula<input inputMode="numeric" maxLength={10} style={S.input} value={cedula} onChange={e => setCedula(e.target.value.replace(/\D/g, ''))} /></label>
                    </div>
                    {/* El pad trae su propio botón "Borrar firma" (avisa por onChange): no se repite aquí. */}
                    <SignaturePad ref={pad} onChange={v => setConTrazo(!v)} />
                    <div style={S.botones}>
                      <button style={boton('primario', ocupado || faltan.length > 0)} disabled={ocupado || faltan.length > 0} onClick={aprobar}>Aprobar y firmar</button>
                    </div>
                  </>) : (<>
                    <p style={S.nota}>Imprime el formato, fírmalo a mano y sube el escaneo firmado.</p>
                    <div style={S.botones}><button style={boton()} onClick={() => pdfBorrador(true)}>Imprimir para firma manual</button></div>
                    <label style={S.etiqueta}>Escaneo firmado (PDF, PNG o JPEG)
                      <input type="file" accept={ACEPTA_ARCHIVO} onChange={e => setEscaneo(e.target.files?.[0] ?? null)} style={{ color: '#cbd5e1', maxWidth: '100%' }} />
                    </label>
                    <button style={boton('primario', ocupado || faltan.length > 0)} disabled={ocupado || faltan.length > 0} onClick={aprobar}>Aprobar con el escaneo</button>
                  </>)}
                  <p style={S.nota}>{TEXTO_FIRMA_APROBACION}</p>
                </div>
              )}

              <div style={S.bloque}>
                <h3 style={S.h3}>Versiones aprobadas ({versiones.length})</h3>
                {!versiones.length && <p style={S.nota}>Todavía no hay versiones aprobadas.</p>}
                {versiones.map(v => (
                  <div key={v.id} style={S.fila}>
                    <div style={{ minWidth: 0, flex: '1 1 200px' }}>
                      <div style={{ ...S.texto, fontWeight: 600 }}>Versión {v.version}{v.id === ultima?.id ? ' (la vigente)' : ''}</div>
                      <div style={S.nota}>{new Date(v.approved_at).toLocaleString('es-EC', { timeZone: 'America/Guayaquil' })} · {v.sign_method === 'pantalla' ? `${v.signer_name} (C.I. ${v.signer_cedula})` : 'formato firmado a mano'} · SHA-256 {v.content_hash.slice(0, 16)}…</div>
                    </div>
                    <div style={S.botones}>
                      <button style={boton()} onClick={() => pdfVersion(v)}>PDF</button>
                      {v.scan_path && <button style={boton()} onClick={() => abrirConUrl(urlArchivo(v.scan_path!))}>Escaneo</button>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
